import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import {
  FaPlus,
  FaUserPlus,
  FaFileExport,
  FaArrowLeft,
  FaEnvelope,
  FaPencilAlt,
  FaRegCalendarAlt,
  FaEllipsisV,
  FaTimes,
  FaRegUserCircle,
  FaUserFriends,
  FaShieldAlt,
  FaRegClock,
  FaMoneyCheckAlt,
  FaChevronLeft,
  FaChevronRight,
  FaUser,
  FaMapMarkerAlt,
  FaUserTie,
  FaRegCircle,
  FaCamera,
} from "react-icons/fa";
import "../styles/AgentsPage.css";
import AppDropdown from "../components/AppDropdown";
import "../styles/UserManagement.css";
import { useNavigate } from "react-router-dom";
import {
  markAgentPaymentDone,
  createAgent,
  listOrgAgents,
  getAgentsDashboardSummary,
  getAgentDetailsFull,
  getAgentProfileDashboard,
  getAgentProfileMerchantPortfolio,
  getAgentProfileRecentActivity,
  releaseAgentPayout,
  suspendAgent,
  activateAgent,
  exportAllAgentsCsv,
  exportAgentMerchantsCsv,
  exportAgentProfilePdf,
} from "../api/api";
import { downloadBlobResponse } from "../utils/download";
import { getUserData, getOrgId } from "../utils/authStorage";
import { useToast } from "../context/ToastContext";
import useSidebarAction from "../hooks/useSidebarAction";

/* ── helpers ── */
const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("") || "AG";

const AVATAR_COLORS = ["#6366f1", "#0ea5e9", "#14b8a6", "#f59e0b", "#ec4899", "#8b5cf6"];
const colorFor = (str = "") => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};

const ROWS_PER_PAGE = 10;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const formatRs = (v) => {
  const n = num(v);
  if (n >= 1000000) return `Rs ${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000) return `Rs ${(n / 1000).toFixed(1)}k`;
  return `Rs ${n.toLocaleString()}`;
};
const money = (v) => {
  if (v == null || v === "") return "—";
  if (typeof v === "number") return formatRs(v);
  return String(v);
};
const extractAgents = (res) => {
  const payload = res?.data ?? res;
  const rows =
    (Array.isArray(payload) && payload) ||
    (Array.isArray(payload?.agents) && payload.agents) ||
    (Array.isArray(payload?.data) && payload.data) ||
    (Array.isArray(res?.agents) && res.agents) ||
    [];
  const total =
    num(
      res?.total ??
        res?.totalCount ??
        payload?.total ??
        payload?.totalCount ??
        res?.pagination?.total ??
        payload?.pagination?.total
    ) || rows.length;
  return { rows, total };
};

/* "TODAY, 11:42 AM" / "YESTERDAY, 4:15 PM" / "12 JUL, 4:15 PM" for the drawer timeline */
const formatActivityWhen = (raw) => {
  if (!raw) return "";
  const d = new Date(raw);
  if (isNaN(d)) return String(raw);
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", hour12: true });
  const now = new Date();
  const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dayDiff = Math.round((startOfDay(now) - startOfDay(d)) / 86400000);
  if (dayDiff === 0) return `TODAY, ${time}`.toUpperCase();
  if (dayDiff === 1) return `YESTERDAY, ${time}`.toUpperCase();
  const day = d.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
  return `${day}, ${time}`.toUpperCase();
};

/* normalize GET /agents/agent-details/:agentId?activityLimit=10 */
const extractAgentDetail = (res) => {
  const payload = res?.data ?? res ?? {};
  const info = payload?.agent ?? payload?.details ?? payload?.agentDetails ?? payload ?? {};
  const rawActs =
    (Array.isArray(payload?.activities) && payload.activities) ||
    (Array.isArray(payload?.recentActivity) && payload.recentActivity) ||
    (Array.isArray(payload?.activity) && payload.activity) ||
    (Array.isArray(info?.activities) && info.activities) ||
    (Array.isArray(info?.recentActivity) && info.recentActivity) ||
    [];
  const activities = rawActs.map((act, i) => ({
    key: act._id ?? act.id ?? i,
    when: formatActivityWhen(act.timestamp ?? act.createdAt ?? act.created_at ?? act.date ?? act.time ?? act.when),
    title: act.title ?? act.action ?? act.event ?? act.type ?? "Activity",
    desc: act.description ?? act.desc ?? act.details ?? act.message ?? "",
  }));
  const merchants =
    (Array.isArray(payload?.merchants) && payload.merchants) ||
    (Array.isArray(info?.merchants) && info.merchants) ||
    [];
  return { info, activities, merchants };
};

const STATUS_OPTIONS = [
  { label: "All Status", value: "all" },
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const AGENT_CREATE_SCHEMA = Yup.object({
  name: Yup.string().required("Name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  region: Yup.string().required("Region is required"),
  phoneNumber: Yup.string()
    .matches(/^[0-9]{10,15}$/, "Invalid phone number")
    .required("Phone is required"),
  fatherName: Yup.string().required("Father name is required"),
  cnic: Yup.string()
    .matches(/^[0-9]{5}-[0-9]{7}-[0-9]{1}$/, "Invalid CNIC (00000-0000000-0)")
    .required("CNIC is required"),
  address: Yup.string().required("Address is required"),
  feeValue: Yup.number()
    .typeError("Fee value must be a number")
    .min(0, "Fee value cannot be negative")
    .required("Fee value is required"),
  iban: Yup.string(),
  accountTitle: Yup.string(),
});

const AGENT_STEP_FIELDS = [
  ["name", "email", "region"],
  ["phoneNumber", "fatherName", "cnic", "address"],
  ["feeValue", "iban", "accountTitle"],
];

const AgentsList = ({ isSidebarOpen }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true); // first page paint only
  const [tableLoading, setTableLoading] = useState(false); // list refetches (filters/page/search)
  const [rawAgents, setRawAgents] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentDetail, setAgentDetail] = useState(null);
  const [agentDetailLoading, setAgentDetailLoading] = useState(false);
  const [profileAgent, setProfileAgent] = useState(null);
  const [profileInfo, setProfileInfo] = useState(null); // full agent record (carries feeValue)
  const [profileDash, setProfileDash] = useState(null);
  const [profilePortfolio, setProfilePortfolio] = useState([]);
  const [profileActivity, setProfileActivity] = useState([]);
  const [profileLoading, setProfileLoading] = useState(false);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [profileRefreshKey, setProfileRefreshKey] = useState(0);
  const [confirmAction, setConfirmAction] = useState(null); // { title, message, confirmLabel, onConfirm }
  const [detailTab, setDetailTab] = useState("Activity");
  const [createAgentOpen, setCreateAgentOpen] = useState(false);
  const [agentStep, setAgentStep] = useState(0);
  const [agentPreviewPhoto, setAgentPreviewPhoto] = useState("");

  const userData = getUserData();
  const user = userData?.user || userData?.data || userData || {};
  const orgId = getOrgId() ?? user?.orgId ?? user?.profile?.orgId;

  const [exportingAgents, setExportingAgents] = useState(false);
  const [exportingProfile, setExportingProfile] = useState(false);

  /* Header Export → all agents CSV from the API */
  const handleExportAgents = async () => {
    if (!orgId || exportingAgents) return;
    setExportingAgents(true);
    try {
      const res = await exportAllAgentsCsv(orgId);
      downloadBlobResponse(res, "agents.csv");
    } catch (err) {
      console.error("Error exporting agents:", err);
      showToast({ severity: "error", summary: "Export", detail: "Failed to export agents", life: 3000 });
    } finally {
      setExportingAgents(false);
    }
  };

  /* Profile Export Data → agent Merchant & Revenue PDF + merchants CSV */
  const handleExportAgentData = async () => {
    const id = profileAgent?._id ?? profileAgent?.id ?? profileAgent?.agentId;
    if (!id || exportingProfile) return;
    setExportingProfile(true);
    try {
      const [pdfRes, csvRes] = await Promise.allSettled([
        exportAgentProfilePdf(id),
        exportAgentMerchantsCsv(id),
      ]);
      if (pdfRes.status === "fulfilled") downloadBlobResponse(pdfRes.value, `agent-${id}-report.pdf`);
      else console.error("Error exporting agent PDF:", pdfRes.reason);
      if (csvRes.status === "fulfilled") downloadBlobResponse(csvRes.value, `agent-${id}-merchants.csv`);
      else console.error("Error exporting agent merchants CSV:", csvRes.reason);

      if (pdfRes.status === "rejected" && csvRes.status === "rejected") {
        showToast({ severity: "error", summary: "Export", detail: "Failed to export agent data", life: 3000 });
      } else if (pdfRes.status === "rejected" || csvRes.status === "rejected") {
        showToast({ severity: "warn", summary: "Export", detail: "One of the export files failed to download", life: 3000 });
      }
    } finally {
      setExportingProfile(false);
    }
  };

  /* Sidebar dropdown actions → open the same create modal / run the same export */
  useSidebarAction({
    create: () => {
      setCreateAgentOpen(true);
      setAgentStep(0);
    },
    export: handleExportAgents,
  });

  /* debounce search box before hitting the API */
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  /* reset to first page when filters change */
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, status]);

  /* summary cards — only on mount / explicit refresh, not on table filters */
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    getAgentsDashboardSummary(orgId)
      .then((sumRes) => {
        if (!cancelled) setSummary(sumRes?.data ?? sumRes ?? null);
      })
      .catch((err) => console.error("Error fetching agents summary:", err));
    return () => {
      cancelled = true;
    };
  }, [orgId, refreshKey]);

  /* table list — refetches alone when filters/page/search change */
  useEffect(() => {
    if (!orgId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const fetchList = async () => {
      setTableLoading(true);
      try {
        const listRes = await listOrgAgents({ orgId, page, limit: ROWS_PER_PAGE, status, search: debouncedSearch });
        if (cancelled) return;
        const { rows, total: tot } = extractAgents(listRes);
        setRawAgents(rows);
        setTotal(tot);
      } catch (err) {
        console.error("Error fetching agents:", err);
        if (!cancelled) {
          setRawAgents([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) {
          setTableLoading(false);
          setLoading(false);
        }
      }
    };
    fetchList();
    return () => {
      cancelled = true;
    };
  }, [orgId, page, status, debouncedSearch, refreshKey]);

  const refreshAgents = () => setRefreshKey((k) => k + 1);

  /* agent details drawer — fetch real details + activity when an agent is selected */
  useEffect(() => {
    if (!selectedAgent) {
      setAgentDetail(null);
      return;
    }
    const id = selectedAgent._id ?? selectedAgent.id ?? selectedAgent.agentId;
    if (!id) return;
    let cancelled = false;
    setAgentDetailLoading(true);
    getAgentDetailsFull(id, 10)
      .then((res) => {
        if (!cancelled) setAgentDetail(extractAgentDetail(res));
      })
      .catch((err) => {
        console.error("Error fetching agent details:", err);
        if (!cancelled) setAgentDetail(null);
      })
      .finally(() => {
        if (!cancelled) setAgentDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedAgent]);

  /* agent profile page — dashboard stats/graph, merchant portfolio, recent activity */
  useEffect(() => {
    if (!profileAgent) {
      setProfileDash(null);
      setProfileInfo(null);
      setProfilePortfolio([]);
      setProfileActivity([]);
      return;
    }
    const id = profileAgent._id ?? profileAgent.id ?? profileAgent.agentId;
    if (!id) return;
    let cancelled = false;
    setProfileLoading(true);
    Promise.allSettled([
      getAgentProfileDashboard(id),
      getAgentProfileMerchantPortfolio(id, 5),
      getAgentProfileRecentActivity(id, 10),
      getAgentDetailsFull(id, 1),
    ])
      .then(([dashRes, portRes, actRes, detailRes]) => {
        if (cancelled) return;
        if (dashRes.status === "fulfilled") {
          const p = dashRes.value?.data ?? dashRes.value ?? null;
          setProfileDash(p?.dashboard ?? p?.summary ?? p);
        } else {
          console.error("Error fetching agent profile dashboard:", dashRes.reason);
        }
        if (detailRes.status === "fulfilled") {
          const { info } = extractAgentDetail(detailRes.value);
          setProfileInfo(info ?? null);
        } else {
          console.error("Error fetching agent detail for profile:", detailRes.reason);
        }
        if (portRes.status === "fulfilled") {
          const p = portRes.value?.data ?? portRes.value ?? {};
          setProfilePortfolio(
            (Array.isArray(p) && p) ||
              (Array.isArray(p?.merchants) && p.merchants) ||
              (Array.isArray(p?.portfolio) && p.portfolio) ||
              (Array.isArray(p?.data) && p.data) ||
              []
          );
        } else {
          console.error("Error fetching merchant portfolio:", portRes.reason);
        }
        if (actRes.status === "fulfilled") {
          const p = actRes.value?.data ?? actRes.value ?? {};
          setProfileActivity(
            (Array.isArray(p) && p) ||
              (Array.isArray(p?.activities) && p.activities) ||
              (Array.isArray(p?.recentActivity) && p.recentActivity) ||
              (Array.isArray(p?.data) && p.data) ||
              []
          );
        } else {
          console.error("Error fetching agent recent activity:", actRes.reason);
        }
      })
      .finally(() => {
        if (!cancelled) setProfileLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [profileAgent, profileRefreshKey]);

  /* normalize server agents for display */
  const agents = useMemo(() => {
    return rawAgents.map((a, i) => {
      const st = String(a.status ?? "").toLowerCase();
      const lastRaw = a.lastActive ?? a.lastActiveAt ?? a.updatedAt ?? a.updated_at ?? a.last_seen ?? null;
      let lastActiveDate = "—";
      let lastActiveTime = "";
      if (lastRaw) {
        const d = new Date(lastRaw);
        if (!isNaN(d)) {
          const dd = String(d.getDate()).padStart(2, "0");
          const mm = String(d.getMonth() + 1).padStart(2, "0");
          const yyyy = d.getFullYear();
          lastActiveDate = `${dd}/${mm}/${yyyy}`;
          lastActiveTime = d.toLocaleTimeString(undefined, { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" });
        }
      }
      return {
        ...a,
        _id: a._id ?? a.id ?? a.agentId ?? a.cnic ?? String(i),
        name: a.name ?? a.agentName ?? "Unknown Agent",
        code: a.code ?? (a.cnic ? `AG-${String(a.cnic).slice(-6)}` : `AG-${1000 + i}`),
        status: st === "suspended" ? "inactive" : st || "active",
        merchants: a.merchants ?? a.totalMerchants ?? a.merchantCount ?? "—",
        volume: money(a.volume ?? a.totalVolume ?? a.monthlyVolume),
        commission: money(a.commission ?? a.totalCommission ?? a.commissionEarned),
        payout: money(a.payout ?? a.duePayout ?? a.pendingPayout),
        lastActiveDate,
        lastActiveTime,
      };
    });
  }, [rawAgents]);

  /* server already paginates & filters — render rows as-is */
  const pageRows = agents;
  const totalPages = Math.max(1, Math.ceil(total / ROWS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);

  const totalAgents = num(summary?.totalAgents ?? summary?.total ?? total);
  const activeAgents = num(summary?.activeAgents ?? summary?.active);
  const pendingPayouts = summary?.pendingPayouts ?? summary?.pendingPayout ?? summary?.duePayouts ?? 0;
  const totalCommissions = summary?.totalCommissions ?? summary?.commissions ?? summary?.commissionEarned ?? 0;

  const handleSuspendAgent = async (agent) => {
    const id = agent._id ?? agent.id ?? agent.agentId;
    try {
      const res = await suspendAgent(id);
      if (res?.isSuccess !== false) {
        showToast({ severity: "success", summary: "Suspended", detail: `${agent.name} suspended`, life: 2000 });
        refreshAgents();
      } else {
        showToast({ severity: "error", summary: "Error", detail: res?.message || "Failed to suspend agent", life: 3000 });
      }
    } catch {
      showToast({ severity: "error", summary: "Error", detail: "Something went wrong", life: 3000 });
    }
  };

  const handleActivateAgent = async (agent) => {
    const id = agent._id ?? agent.id ?? agent.agentId;
    try {
      const res = await activateAgent(id);
      if (res?.isSuccess !== false) {
        showToast({ severity: "success", summary: "Activated", detail: `${agent.name} activated`, life: 2000 });
        refreshAgents();
      } else {
        showToast({ severity: "error", summary: "Error", detail: res?.message || "Failed to activate agent", life: 3000 });
      }
    } catch {
      showToast({ severity: "error", summary: "Error", detail: "Something went wrong", life: 3000 });
    }
  };

  const handlePaymentDone = async (agent) => {
    try {
      const res = await markAgentPaymentDone(agent.cnic);
      if (res?.isSuccess) {
        showToast({ severity: "success", summary: "Paid", detail: "Payment completed", life: 2000 });
        refreshAgents();
      }
    } catch {
      showToast({ severity: "error", summary: "Error", detail: "Payment update failed", life: 3000 });
    }
  };

  /* Release Payout → PUT /agents/agent-profile-dashboard/payment-complete?id= */
  const handleReleasePayout = async () => {
    if (!profileAgent) return;
    const id = profileAgent._id ?? profileAgent.id ?? profileAgent.agentId;
    if (!id) return;
    setPayoutLoading(true);
    try {
      const res = await releaseAgentPayout(id);
      if (res?.isSuccess !== false) {
        showToast({ severity: "success", summary: "Payout Released", detail: res?.message || "Payout released successfully", life: 2200 });
        setProfileRefreshKey((k) => k + 1);
        refreshAgents();
      } else {
        showToast({ severity: "error", summary: "Error", detail: res?.message || "Failed to release payout", life: 3000 });
      }
    } catch {
      showToast({ severity: "error", summary: "Error", detail: "Something went wrong", life: 3000 });
    } finally {
      setPayoutLoading(false);
    }
  };

  /* Edit Profile → open CreateAgent page in edit mode with full agent data */
  const handleEditProfile = async () => {
    if (!profileAgent) return;
    const id = profileAgent._id ?? profileAgent.id ?? profileAgent.agentId;
    let agentData = profileAgent;
    try {
      if (id) {
        const res = await getAgentDetailsFull(id, 1);
        const { info } = extractAgentDetail(res);
        agentData = { ...profileAgent, ...info };
      }
    } catch (err) {
      console.error("Error fetching agent for edit:", err);
    }
    navigate("/dashboard/agents?mode=edit", { state: { mode: "edit", user: agentData } });
  };

  const handleCreateAgent = async (values, { resetForm, setSubmitting }) => {
    try {
      const payload = { ...values, orgID: orgId };
      const res = await createAgent(payload);
      if (res?.isSuccess) {
        showToast({ severity: "success", summary: "Created", detail: res.message || "Agent created successfully", life: 2200 });
        resetForm();
        setAgentPreviewPhoto("");
        setCreateAgentOpen(false);
        setAgentStep(0);
        refreshAgents();
      } else {
        showToast({ severity: "error", summary: "Error", detail: res?.message || "Failed to create agent", life: 3000 });
      }
    } catch {
      showToast({ severity: "error", summary: "Error", detail: "Something went wrong", life: 3000 });
    } finally {
      setSubmitting(false);
    }
  };

  const kpis = [
    { label: "Total Agents", value: Number(totalAgents).toLocaleString(), icon: <FaUserFriends />, chip: "0%", accent: "#6366f1" },
    { label: "Active Agents", value: Number(activeAgents).toLocaleString(), icon: <FaShieldAlt />, chip: "0%", accent: "#14b8a6" },
    { label: "Pending Payouts", value: money(pendingPayouts), icon: <FaRegClock />, chip: "0%", chipMuted: true, accent: "#f59e0b" },
    { label: "Total Commissions", value: money(totalCommissions), icon: <FaMoneyCheckAlt />, chip: "0%", accent: "#0ea5e9" },
  ];

  const detailInfo = agentDetail?.info ?? {};
  const detailActivities = agentDetail?.activities ?? [];
  const detailMerchants = agentDetail?.merchants ?? [];

  /* performance growth graph — bars = revenue, line = commission */
  const growthData = useMemo(() => {
    const d = profileDash ?? {};
    const rows =
      (Array.isArray(d.performanceGrowth) && d.performanceGrowth) ||
      (Array.isArray(d.performance) && d.performance) ||
      (Array.isArray(d.growth) && d.growth) ||
      (Array.isArray(d.monthlyPerformance) && d.monthlyPerformance) ||
      (Array.isArray(d.chart) && d.chart) ||
      [];
    return rows.map((g) => ({
      label: g.month ?? g.label ?? g.name ?? "",
      revenue: num(g.revenue ?? g.netRevenue ?? g.volume ?? g.value ?? g.amount),
      commission: num(g.commission ?? g.commissionEarned ?? g.commissions),
    }));
  }, [profileDash]);

  const profileBars = useMemo(() => {
    /* empty performanceGrowth → still draw a 12-month chart with zeroed bars */
    if (!growthData.length) return Array.from({ length: 12 }, () => 4);
    const maxRev = Math.max(...growthData.map((g) => g.revenue), 1);
    return growthData.map((g) => Math.max(4, Math.round((g.revenue / maxRev) * 90)));
  }, [growthData]);

  const profileLinePath = useMemo(() => {
    if (growthData.length < 2) return "";
    const maxCom = Math.max(...growthData.map((g) => g.commission), 1);
    const pts = growthData.map((g, i) => {
      const x = Math.round((i / (growthData.length - 1)) * 1000);
      const y = Math.round(230 - (g.commission / maxCom) * 200);
      return `${x},${y}`;
    });
    return `M${pts[0]} L${pts.slice(1).join(" L")}`;
  }, [growthData]);

  const profileRecentActivity = profileActivity.map((a, i) => ({
    key: a._id ?? a.id ?? i,
    title: a.title ?? a.action ?? a.event ?? a.description ?? "Activity",
    time: formatActivityWhen(a.timestamp ?? a.createdAt ?? a.created_at ?? a.date ?? a.time),
    tag: a.tag ?? a.actionId ?? a.status ?? a.type ?? "",
  }));

  const profileMerchants = profilePortfolio.map((m, i) => ({
    key: m._id ?? m.id ?? m.merchantID ?? i,
    name: m.name ?? m.businessName ?? m.merchantName ?? "Merchant",
    type: m.category ?? m.businessType ?? m.type ?? "—",
    volume: `${money(m.volume ?? m.totalVolume ?? m.monthlyVolume ?? 0)} Vol`,
    status: m.status ?? m.tier ?? "",
  }));

  const dash = profileDash ?? {};
  const profileMerchantCount = num(dash.merchantCount ?? dash.totalMerchants ?? (profileAgent?.merchants !== "—" ? profileAgent?.merchants : 0));
  const profileMonthlyVolume = money(dash.monthlyVolume ?? dash.volume ?? (profileAgent?.volume !== "—" ? profileAgent?.volume : 0));
  const profileTotalRevenue = money(dash.totalRevenue ?? dash.revenue ?? dash.lifetimeRevenue ?? 0);
  const profileCommission = money(dash.commissionEarned ?? dash.totalCommission ?? dash.commission ?? (profileAgent?.commission !== "—" ? profileAgent?.commission : 0));

  /* Invoice "Rate" should show the agent's own configured fee (set at creation),
     not the backend invoice default. Fall back to the invoice rate only if the
     agent record carries no fee. */
  const agentFeeRaw =
    profileInfo?.feeValue ??
    profileInfo?.fee ??
    profileAgent?.feeValue ??
    profileAgent?.fee ??
    dash.feeValue ??
    dash.fee ??
    null;
  const agentFeeType = String(
    profileInfo?.feeType ?? profileAgent?.feeType ?? dash.feeType ?? ""
  ).toUpperCase();
  const invoiceRate =
    agentFeeRaw != null && agentFeeRaw !== ""
      ? agentFeeType === "F" || agentFeeType === "FIXED"
        ? money(agentFeeRaw)
        : `${agentFeeRaw}%`
      : dash.invoicePreview?.rate ?? "—";
  const profileCollectionPerf =
    dash.collectionPerformance != null || dash.collectionPerf != null
      ? `${num(dash.collectionPerformance ?? dash.collectionPerf).toFixed(1)}%`
      : "—";
  const profileSettlements =
    (Array.isArray(dash.pendingSettlements) && dash.pendingSettlements) ||
    (Array.isArray(dash.settlements) && dash.settlements) ||
    [];
  const rawInsight = dash.strategicInsight ?? dash.insight ?? null;
  const profileInsight =
    typeof rawInsight === "string" ? rawInsight : rawInsight?.text ?? rawInsight?.message ?? null;

  /* Agent detail fields for the side column (from the full agent record) */
  const info = profileInfo ?? {};
  const agentDetailRows = [
    { label: "Email", value: info.email ?? profileAgent?.email },
    { label: "CNIC", value: info.cnic ?? profileAgent?.cnic },
    { label: "Region", value: info.region ?? info.city ?? profileAgent?.region },
    {
      label: "Phone",
      value: info.phoneNumber ?? info.phone ?? info.contact ?? profileAgent?.phoneNumber,
    },
  ];

  /* centered confirm popup (replaces browser window.confirm) */
  const confirmModal = confirmAction && (
    <div className="ag-confirm-overlay" onClick={() => setConfirmAction(null)}>
      <div className="ag-confirm" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <h4>{confirmAction.title}</h4>
        <p>{confirmAction.message}</p>
        <div className="ag-confirm__actions">
          <button className="ag-btn" type="button" onClick={() => setConfirmAction(null)}>
            Cancel
          </button>
          <button
            className="ag-btn ag-btn--primary"
            type="button"
            onClick={() => {
              const fn = confirmAction.onConfirm;
              setConfirmAction(null);
              fn?.();
            }}
          >
            {confirmAction.confirmLabel ?? "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="ag-page">
        <p className="ag-muted">Loading agents…</p>
      </div>
    );
  }

  if (profileAgent) {
    return (
      <div className="ag-page ag-page--detail">
        <button
          type="button"
          className="ag-back"
          onClick={() => setProfileAgent(null)}
        >
          <FaArrowLeft /> Back to Agents
        </button>

        <div className="ag-profile-head">
          <div className="ag-profile-agent">
            <div className="ag-profile-avatar" style={{ background: colorFor(profileAgent.name) }}>
              {initials(profileAgent.name)}
            </div>
            <div>
              <h2>{profileAgent.name}</h2>
              <div className="ag-profile-meta">
                ID: {profileAgent.code} • <span>{profileAgent.status === "inactive" ? "Inactive" : "Active"} Status</span>
              </div>
            </div>
          </div>

          <div className="ag-profile-actions">
            <button className="ag-btn" type="button">
              <FaEnvelope /> Message Agent
            </button>
            <button className="ag-btn" type="button" onClick={handleEditProfile}>
              <FaPencilAlt /> Edit Profile
            </button>
            <button className="ag-btn ag-btn--primary" type="button" onClick={handleExportAgentData} disabled={exportingProfile}>
              <FaFileExport /> {exportingProfile ? "Exporting…" : "Export Data"}
            </button>
          </div>
        </div>

        <div className="ag-profile-stats">
          <div className="ag-profile-stat">
            <label>Merchant Count</label>
            <h3>{profileMerchantCount.toLocaleString()}</h3>
            <p>0%</p>
          </div>
          <div className="ag-profile-stat">
            <label>Monthly Volume</label>
            <h3>{profileMonthlyVolume}</h3>
            <p>0%</p>
          </div>
          <div className="ag-profile-stat">
            <label>Total Revenue</label>
            <h3>{profileTotalRevenue}</h3>
            <p>→ Stable</p>
          </div>
          <div className="ag-profile-stat">
            <label>Commission Earned</label>
            <h3>{profileCommission}</h3>
            <p>↗ Top 1%</p>
          </div>
          <div className="ag-profile-stat ag-profile-stat--ring">
            <label>Collection Perf.</label>
            <h3>{profileCollectionPerf}</h3>
            <p>◎ Elite</p>
            <span className="ag-profile-ring" />
          </div>
        </div>

        <div className="ag-profile-grid">
          <div className="ag-profile-maincol">
            <div className="ag-profile-card ag-profile-card--chart">
              <div className="ag-profile-card__head">
                <div>
                  <h4>Performance Growth</h4>
                  <span>Net revenue vs commission across 12 months</span>
                </div>
              </div>

              <div className="ag-growth">
                {profileLoading && growthData.length === 0 ? (
                  <p className="ag-muted" style={{ margin: "auto" }}>Loading performance…</p>
                ) : (
                  <>
                    <div className="ag-growth__bars">
                      {profileBars.map((bar, idx) => (
                        <div key={idx} className="ag-growth__bar" style={{ height: `${bar}%` }} />
                      ))}
                    </div>
                    {profileLinePath && (
                      <svg className="ag-growth__line" viewBox="0 0 1000 240" preserveAspectRatio="none">
                        <path d={profileLinePath} />
                      </svg>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="ag-profile-card">
              <div className="ag-profile-card__head ag-profile-card__head--spread">
                <h4>Merchant Portfolio</h4>
                <button className="ag-inline-btn" type="button">
                  View All {profileMerchantCount.toLocaleString()} Merchants
                </button>
              </div>
              <div className="ag-merchant-tiles">
                {profileMerchants.length === 0 ? (
                  <p className="ag-muted">{profileLoading ? "Loading merchants…" : "No merchants onboarded yet."}</p>
                ) : (
                  profileMerchants.map((merchant) => (
                    <div key={merchant.key} className="ag-merchant-tile">
                      <div>
                        <strong>{merchant.name}</strong>
                        <span>{merchant.type}</span>
                      </div>
                      <div className="ag-merchant-meta">
                        <em>{merchant.volume}</em>
                        <small>{merchant.status}</small>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="ag-profile-card">
              <div className="ag-profile-card__head">
                <h4>Recent Activity</h4>
              </div>
              <div className="ag-profile-activity">
                {profileRecentActivity.length === 0 ? (
                  <p className="ag-muted">{profileLoading ? "Loading activity…" : "No recent activity found."}</p>
                ) : (
                  profileRecentActivity.map((activity) => (
                    <div key={activity.key} className="ag-profile-activity__row">
                      <div>
                        <strong>{activity.title}</strong>
                        <span>{activity.time}</span>
                      </div>
                      <small>{activity.tag}</small>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="ag-profile-sidecol">
            {/* 1 — Remaining Merchant Transactions (summary + payout action) */}
            <div className="ag-profile-card ag-profile-card--invoice">
              <div className="ag-profile-card__head">
                <div>
                  <h4>Remaining Merchant Transactions</h4>
                  <span>{dash.invoicePreview?.invoiceNo ?? "—"}</span>
                </div>
              </div>
              <div className="ag-invoice">
                <div className="ag-invoice__row"><span>Merchants</span><b>{profileMerchantCount}</b></div>
                <div className="ag-invoice__row"><span>Pending Transactions</span><b>{num(dash.pendingTransactions ?? dash.remainingTransactions ?? dash.invoicePreview?.transactions ?? 0)}</b></div>
                <div className="ag-invoice__total"><span>Remaining Payable</span><b>{money(dash.invoicePreview?.totalPayable ?? 0)}</b></div>
              </div>
            </div>

            {/* 2 — Agent detail */}
            <div className="ag-profile-card ag-profile-card--agentdetail">
              <div className="ag-profile-card__head">
                <h4>Agent Detail</h4>
              </div>
              <div className="ag-invoice">
                <div className="ag-invoice__row"><span>Name</span><b>{info.name ?? profileAgent.name}</b></div>
                {agentDetailRows.map((row) => (
                  <div key={row.label} className="ag-invoice__row">
                    <span>{row.label}</span>
                    <b>{row.value || "—"}</b>
                  </div>
                ))}
              </div>
            </div>

            {/* 3 — Payout invoice */}
            <div className="ag-profile-card ag-profile-card--payoutinvoice">
              <div className="ag-profile-card__head">
                <div>
                  <h4>Payout Invoice</h4>
                  <span>{dash.invoicePreview?.invoiceNo ?? "—"}</span>
                </div>
              </div>
              <div className="ag-invoice">
                <div className="ag-invoice__row"><span>Bill To</span><b>{dash.invoicePreview?.billTo ?? profileAgent.name}</b></div>
                <div className="ag-invoice__row"><span>Rate</span><b>{invoiceRate}</b></div>
                <div className="ag-invoice__row"><span>Earned</span><b>{money(dash.invoicePreview?.earned ?? 0)}</b></div>
                <div className="ag-invoice__total"><span>Total Payable</span><b>{money(dash.invoicePreview?.totalPayable ?? 0)}</b></div>
              </div>
              <button
                className="ag-btn ag-btn--primary ag-fullbtn"
                type="button"
                disabled={payoutLoading}
                onClick={() =>
                  setConfirmAction({
                    title: "Release Payout",
                    message: `Release payout for ${profileAgent.name}?`,
                    confirmLabel: "Release",
                    onConfirm: handleReleasePayout,
                  })
                }
              >
                {payoutLoading ? "Releasing…" : "Release Payout"}
              </button>
            </div>

            {/* 4 — Agent pending payment */}
            <div className="ag-profile-card ag-profile-card--settlements">
              <div className="ag-profile-card__head">
                <h4>Agent Pending Payment</h4>
              </div>
              {profileSettlements.length === 0 ? (
                <p className="ag-muted">No pending payments.</p>
              ) : (
                profileSettlements.map((s, idx) => (
                  <div key={s._id ?? s.id ?? idx} className="ag-settlement-row">
                    <div>
                      <strong>{s.title ?? s.cycle ?? s.name ?? `Cycle #${idx + 1}`}</strong>
                      <span>Est: {money(s.estimate ?? s.est ?? s.amount ?? 0)}</span>
                    </div>
                    <small>{s.dueIn ?? s.due ?? s.eta ?? ""}</small>
                  </div>
                ))
              )}
            </div>

            {profileInsight && (
              <div className="ag-profile-card ag-profile-card--insight">
                <h4>Strategic Insight</h4>
                <p>{profileInsight}</p>
              </div>
            )}
          </div>
        </div>
        {confirmModal}
      </div>
    );
  }

  return (
    <div className="ag-page">
      {/* Header */}
      <div className="ag-head">
        <div>
          <h1 className="ag-head__title">Agents</h1>
          <div className="ag-head__sub">
            Manage your global network of financial representatives.
          </div>
        </div>
        <div className="ag-head__actions">
          <button className="ag-btn" type="button" onClick={handleExportAgents} disabled={exportingAgents}>
            <FaFileExport /> {exportingAgents ? "Exporting…" : "Export"}
          </button>
          <button
            className="ag-btn ag-btn--primary"
            type="button"
            onClick={() => { setCreateAgentOpen(true); setAgentStep(0); }}
          >
            <FaUserPlus /> Create Agent
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="ag-kpis">
        {kpis.map((k) => (
          <div key={k.label} className="ag-kpi">
            <div className="ag-kpi__top">
              <div className="ag-kpi__icon" style={{ color: k.accent }}>{k.icon}</div>
              <div className={`ag-kpi__chip ${k.chipMuted ? "ag-kpi__chip--muted" : ""}`}>{k.chip}</div>
            </div>
            <div className="ag-kpi__label">{k.label}</div>
            <div className="ag-kpi__value">{k.value}</div>
            <div className="ag-kpi__bar" style={{ background: k.accent }} />
          </div>
        ))}
      </div>

      {/* Table card */}
      <div className="ag-tablecard">
        <div className="ag-toolbar">
          <div className="ag-toolbar__left">
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <AppDropdown
                options={STATUS_OPTIONS}
                value={status}
                onChange={(v) => setStatus(v)}
              />
            </span>
            <button className="ag-pillbtn" type="button">
              <FaRegCalendarAlt /> Last 30 Days
            </button>
            <input
              className="ag-pillbtn"
              style={{ minWidth: 200 }}
              placeholder="Search agents, IDs, or merchants…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="ag-toolbar__right">
            <span className="ag-selinfo">Selected: <b>0</b></span>
            <button className="ag-pillbtn" type="button">Bulk Actions</button>
          </div>
        </div>

        <div className="ag-tablewrap">
          <table className="ag-table">
            <thead>
              <tr>
                <th style={{ width: 44 }}><input type="checkbox" className="ag-check" /></th>
                <th>Agent</th>
                <th>Status</th>
                <th>Merchants</th>
                <th>Volume</th>
                <th>Commission</th>
                <th>Due Payout</th>
                <th>Last Active</th>
                <th style={{ width: 44 }}></th>
              </tr>
            </thead>
            <tbody>
              {tableLoading ? (
                <tr>
                  <td colSpan={9} className="ag-muted" style={{ textAlign: "center", padding: 30 }}>
                    Loading agents…
                  </td>
                </tr>
              ) : pageRows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="ag-muted" style={{ textAlign: "center", padding: 30 }}>
                    No agents found.
                  </td>
                </tr>
              ) : (
                pageRows.map((a) => (
                  <tr
                    key={a._id}
                    className="ag-row--clickable"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setProfileAgent(a);
                      setSelectedAgent(null);
                    }}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" className="ag-check" />
                    </td>
                    <td>
                      <div className="ag-agent">
                        <div className="ag-avatar" style={{ background: colorFor(a.name) }}>
                          {initials(a.name)}
                        </div>
                        <div>
                          <button
                            type="button"
                            className="ag-agent__link"
                            onClick={() => {
                              setProfileAgent(a);
                              setSelectedAgent(null);
                            }}
                          >
                            {a.name}
                          </button>
                          <div className="ag-agent__id">{a.code}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`ag-status ag-status--${a.status === "pending" ? "pending" : a.status === "inactive" ? "inactive" : "active"}`}>
                        {a.status}
                      </span>
                    </td>
                    <td>{a.merchants}</td>
                    <td>{a.volume}</td>
                    <td className="ag-commission">{a.commission}</td>
                    <td className={a.payout === "Rs 0" || a.payout === "—" ? "ag-muted" : "ag-payout"}>{a.payout}</td>
                    <td className="ag-muted">
                      {a.lastActiveDate === "—" ? (
                        "—"
                      ) : (
                        <div>
                          <div>{a.lastActiveDate}</div>
                          <div style={{ opacity: 0.75 }}>{a.lastActiveTime}</div>
                        </div>
                      )}
                    </td>
                    <td>
                      <button
                        className="ag-rowmenu"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedAgent(a);
                          setDetailTab("Activity");
                        }}
                      >
                        <FaEllipsisV />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="ag-foot">
          <div className="ag-foot__info">
            Showing <b>{total ? (currentPage - 1) * ROWS_PER_PAGE + 1 : 0}-
            {Math.min((currentPage - 1) * ROWS_PER_PAGE + pageRows.length, total)}</b> of{" "}
            <b>{Number(total).toLocaleString()}</b> agents
          </div>
          <div className="ag-pager">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>
              <FaChevronLeft />
            </button>
            {Array.from({ length: Math.min(3, totalPages) }, (_, i) => i + 1).map((n) => (
              <button key={n} className={n === currentPage ? "active" : ""} onClick={() => setPage(n)}>
                {n}
              </button>
            ))}
            {totalPages > 3 && <button disabled>…</button>}
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
              <FaChevronRight />
            </button>
          </div>
        </div>
      </div>

      {/* Floating add (mobile convenience) */}
      <button
        className="ag-btn ag-btn--primary"
        style={{ display: "none" }}
        onClick={() => navigate("/dashboard/agents?mode=create")}
      >
        <FaPlus />
      </button>

      {createAgentOpen && (
        <div className="um-modaloverlay" role="dialog" aria-modal="true" aria-label="Create Agent">
          <button
            type="button"
            className="um-modaloverlay__scrim"
            aria-label="Close"
            onClick={() => { setCreateAgentOpen(false); setAgentStep(0); setAgentPreviewPhoto(""); }}
          />
          <Formik
            initialValues={{ name: "", email: "", region: "", phoneNumber: "", fatherName: "", cnic: "", address: "", feeValue: "", iban: "", accountTitle: "" }}
            validationSchema={AGENT_CREATE_SCHEMA}
            onSubmit={handleCreateAgent}
            validateOnChange={false}
            validateOnBlur
          >
            {({ values, errors, touched, setFieldValue, setTouched, validateForm, submitForm, isSubmitting }) => (
              <Form
                className="um-adddialog-shell um-adddialog-shell--overlay"
                onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
              >
                <div className="um-adddialog">

                  {/* Header */}
                  <div className="um-adddialog__header">
                    <div>
                      <h2 className="um-adddialog__title">Add Agent</h2>
                      <div className="um-adddialog__subtitle">Onboard a new field representative to your organization.</div>
                    </div>
                    <button
                      type="button"
                      className="um-adddialog__close"
                      onClick={() => { setCreateAgentOpen(false); setAgentStep(0); setAgentPreviewPhoto(""); }}
                    >
                      <FaTimes />
                    </button>
                  </div>

                  {/* Stepper */}
                  <div className="um-adddialog__steps" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
                    {[["Basic Info", 0], ["Identity", 1], ["Banking", 2]].map(([label, idx]) => (
                      <div key={label} className={`um-adddialog__step ${agentStep === idx ? "is-active" : ""}`}>
                        <span className="um-adddialog__stepIndex">{idx + 1}</span>
                        {label}
                      </div>
                    ))}
                  </div>

                  {/* Body */}
                  <div className="um-adddialog__body">

                    {/* Step 0 — Basic Info */}
                    {agentStep === 0 && (
                      <div className="um-adddialog__grid">
                        <div className="um-adddialog__field um-adddialog__field--full" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, marginBottom: 4 }}>
                          <div
                            style={{
                              width: 72, height: 72, borderRadius: "50%",
                              background: agentPreviewPhoto ? "transparent" : "rgba(99,102,241,0.18)",
                              border: "2px dashed rgba(99,102,241,0.4)",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              overflow: "hidden", flexShrink: 0,
                            }}
                          >
                            {agentPreviewPhoto
                              ? <img src={agentPreviewPhoto} alt="preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              : <FaCamera style={{ fontSize: 22, color: "rgba(99,102,241,0.7)" }} />
                            }
                          </div>
                          <label
                            htmlFor="ag-dialog-photo"
                            style={{
                              cursor: "pointer", fontSize: 11, fontWeight: 700,
                              letterSpacing: "0.06em", color: "rgba(129,140,248,0.9)",
                              background: "rgba(99,102,241,0.12)",
                              border: "1px solid rgba(99,102,241,0.25)",
                              borderRadius: 6, padding: "4px 14px",
                            }}
                          >
                            {agentPreviewPhoto ? "Change Photo" : "Upload Photo"}
                          </label>
                          <input
                            id="ag-dialog-photo"
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) setAgentPreviewPhoto(URL.createObjectURL(file));
                            }}
                          />
                          {agentPreviewPhoto && (
                            <button
                              type="button"
                              style={{ background: "none", border: "none", fontSize: 11, color: "rgba(195,197,217,0.6)", cursor: "pointer", padding: 0 }}
                              onClick={() => setAgentPreviewPhoto("")}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        <div className="um-adddialog__field">
                          <label className="um-adddialog__label">AGENT NAME</label>
                          <div className="um-forminputwrap">
                            <FaUser className="um-forminputwrap__icon" />
                            <Field name="name" type="text" className="um-adddialog__input" placeholder="e.g. Jordan Diaz" />
                          </div>
                          {touched.name && errors.name ? <div className="error-text">{errors.name}</div> : <div className="error-text" />}
                        </div>

                        <div className="um-adddialog__field">
                          <label className="um-adddialog__label">REGION</label>
                          <div className="um-forminputwrap">
                            <FaMapMarkerAlt className="um-forminputwrap__icon" />
                            <Field name="region" type="text" className="um-adddialog__input" placeholder="e.g. Karachi South" />
                          </div>
                          {touched.region && errors.region ? <div className="error-text">{errors.region}</div> : <div className="error-text" />}
                        </div>

                        <div className="um-adddialog__field um-adddialog__field--full">
                          <label className="um-adddialog__label">ENTERPRISE EMAIL</label>
                          <div className="um-forminputwrap">
                            <FaEnvelope className="um-forminputwrap__icon" />
                            <Field name="email" type="email" className="um-adddialog__input" placeholder="agent@company.com" />
                          </div>
                          {touched.email && errors.email ? <div className="error-text">{errors.email}</div> : <div className="error-text" />}
                        </div>
                      </div>
                    )}

                    {/* Step 1 — Identity */}
                    {agentStep === 1 && (
                      <div className="um-adddialog__grid">
                        <div className="um-adddialog__field">
                          <label className="um-adddialog__label">PHONE NUMBER</label>
                          <div className="um-adddialog__field um-adddialog__field--nomargin">
                            <PhoneInput
                              country="pk"
                              value={values.phoneNumber}
                              onChange={(value) => setFieldValue("phoneNumber", value.replace(/\D/g, ""))}
                              inputClass="um-adddialog__input"
                              countryCodeEditable={false}
                            />
                          </div>
                          {touched.phoneNumber && errors.phoneNumber ? <div className="error-text">{errors.phoneNumber}</div> : <div className="error-text" />}
                        </div>

                        <div className="um-adddialog__field">
                          <label className="um-adddialog__label">FATHER NAME</label>
                          <div className="um-forminputwrap">
                            <FaUserTie className="um-forminputwrap__icon" />
                            <Field name="fatherName" type="text" className="um-adddialog__input" placeholder="Father's full name" />
                          </div>
                          {touched.fatherName && errors.fatherName ? <div className="error-text">{errors.fatherName}</div> : <div className="error-text" />}
                        </div>

                        <div className="um-adddialog__field">
                          <label className="um-adddialog__label">CNIC</label>
                          <div className="um-forminputwrap">
                            <FaRegCircle className="um-forminputwrap__icon" />
                            <input
                              className="um-adddialog__input"
                              placeholder="00000-0000000-0"
                              maxLength={15}
                              value={values.cnic}
                              onChange={(e) => {
                                let v = e.target.value.replace(/[^0-9]/g, "");
                                if (v.length > 5) v = v.slice(0, 5) + "-" + v.slice(5);
                                if (v.length > 13) v = v.slice(0, 13) + "-" + v.slice(13, 14);
                                setFieldValue("cnic", v);
                              }}
                            />
                          </div>
                          {touched.cnic && errors.cnic ? <div className="error-text">{errors.cnic}</div> : <div className="error-text" />}
                        </div>

                        <div className="um-adddialog__field">
                          <label className="um-adddialog__label">ADDRESS</label>
                          <div className="um-forminputwrap">
                            <FaMapMarkerAlt className="um-forminputwrap__icon" />
                            <Field name="address" type="text" className="um-adddialog__input" placeholder="Street, City" />
                          </div>
                          {touched.address && errors.address ? <div className="error-text">{errors.address}</div> : <div className="error-text" />}
                        </div>

                        <div className="um-adddialog__field">
                          <label className="um-adddialog__label">FEE VALUE</label>
                          <div className="um-forminputwrap">
                            <FaRegCircle className="um-forminputwrap__icon" />
                            <Field name="feeValue" type="number" className="um-adddialog__input" placeholder="Enter fee value" />
                          </div>
                          {touched.feeValue && errors.feeValue ? <div className="error-text">{errors.feeValue}</div> : <div className="error-text" />}
                        </div>
                      </div>
                    )}

                    {/* Step 2 — Banking */}
                    {agentStep === 2 && (
                      <div className="um-adddialog__grid">
                        <div className="um-adddialog__field um-adddialog__field--full">
                          <label className="um-adddialog__label">IBAN</label>
                          <div className="um-forminputwrap">
                            <FaRegCircle className="um-forminputwrap__icon" />
                            <Field name="iban" type="text" className="um-adddialog__input" placeholder="PK00ABCD0123456789" />
                          </div>
                          {touched.iban && errors.iban ? <div className="error-text">{errors.iban}</div> : <div className="error-text" />}
                        </div>

                        <div className="um-adddialog__field um-adddialog__field--full">
                          <label className="um-adddialog__label">ACCOUNT TITLE</label>
                          <div className="um-forminputwrap">
                            <FaUserTie className="um-forminputwrap__icon" />
                            <Field name="accountTitle" type="text" className="um-adddialog__input" placeholder="Account holder name" />
                          </div>
                          {touched.accountTitle && errors.accountTitle ? <div className="error-text">{errors.accountTitle}</div> : <div className="error-text" />}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="um-adddialog__footer">
                    <button
                      type="button"
                      className="um-adddialog__cancel"
                      onClick={() => { setCreateAgentOpen(false); setAgentStep(0); setAgentPreviewPhoto(""); }}
                    >
                      Cancel
                    </button>
                    <div className="um-adddialog__actions">
                      {agentStep > 0 && (
                        <button
                          type="button"
                          className="um-adddialog__back"
                          onClick={() => setAgentStep((s) => s - 1)}
                        >
                          Back
                        </button>
                      )}
                      {agentStep < 2 ? (
                        <button
                          key="ag-next"
                          type="button"
                          className="um-adddialog__submit"
                          onClick={async () => {
                            const fields = AGENT_STEP_FIELDS[agentStep];
                            const touchObj = {};
                            fields.forEach((f) => { touchObj[f] = true; });
                            setTouched(touchObj, false);
                            const errs = await validateForm();
                            if (fields.every((f) => !errs[f])) setAgentStep((s) => s + 1);
                          }}
                        >
                          Next →
                        </button>
                      ) : (
                        <button
                          key="ag-submit"
                          type="button"
                          className="um-adddialog__submit"
                          disabled={isSubmitting}
                          onClick={() => submitForm()}
                        >
                          {isSubmitting ? "Creating..." : "Create Agent"}
                        </button>
                      )}
                    </div>
                  </div>

                </div>
              </Form>
            )}
          </Formik>
        </div>
      )}

      {selectedAgent && (
        <div className="ag-details-overlay" onClick={() => setSelectedAgent(null)}>
          <aside className="ag-details" onClick={(e) => e.stopPropagation()}>
            <div className="ag-details__head">
              <div className="ag-details__title"><FaRegUserCircle /> Agent Details</div>
              <button className="ag-details__close" onClick={() => setSelectedAgent(null)}>
                <FaTimes />
              </button>
            </div>

            <div className="ag-details__body">
              <div className="ag-details__profile">
                <div className="ag-details__avatar" style={{ background: colorFor(selectedAgent.name) }}>
                  {initials(selectedAgent.name)}
                </div>
                <div>
                  <div className="ag-details__name">{detailInfo.name ?? selectedAgent.name}</div>
                  <div className="ag-details__meta">
                    {[
                      detailInfo.designation ?? detailInfo.role ?? "Field Agent",
                      detailInfo.region ?? detailInfo.city ?? selectedAgent.region,
                    ]
                      .filter(Boolean)
                      .join(" • ")}
                  </div>
                  {(detailInfo.tag ?? detailInfo.badge ?? detailInfo.performanceTag) && (
                    <span className="ag-details__tag">{detailInfo.tag ?? detailInfo.badge ?? detailInfo.performanceTag}</span>
                  )}
                </div>
              </div>

              <div className="ag-details__stats">
                <div className="ag-details__stat">
                  <div className="ag-details__stat-label">Lifetime Revenue</div>
                  <div className="ag-details__stat-value">
                    {money(
                      detailInfo.lifetimeRevenue ??
                        detailInfo.totalRevenue ??
                        detailInfo.totalVolume ??
                        detailInfo.volume ??
                        (selectedAgent.volume !== "—" ? selectedAgent.volume : 0)
                    )}
                  </div>
                </div>
                <div className="ag-details__stat">
                  <div className="ag-details__stat-label">Conversion Rate</div>
                  <div className="ag-details__stat-value ag-details__stat-value--mint">
                    {detailInfo.conversionRate != null && detailInfo.conversionRate !== ""
                      ? `${num(detailInfo.conversionRate).toFixed(1)}%`
                      : "—"}
                  </div>
                </div>
              </div>

              <div className="ag-details__tabs">
                {["Activity", "Merchants", "Compliance"].map((tab) => (
                  <button
                    key={tab}
                    className={`ag-details__tab ${detailTab === tab ? "active" : ""}`}
                    onClick={() => setDetailTab(tab)}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {detailTab === "Activity" && (
                agentDetailLoading ? (
                  <div className="ag-details__placeholder">Loading activity…</div>
                ) : detailActivities.length === 0 ? (
                  <div className="ag-details__placeholder">No recent activity found.</div>
                ) : (
                  <div className="ag-details__timeline">
                    {detailActivities.map((item, idx) => (
                      <div key={item.key} className="ag-details__event">
                        <span className={`ag-details__dot ${idx === 0 ? "active" : ""}`} />
                        <div>
                          <div className="ag-details__when">{item.when}</div>
                          <div className="ag-details__event-title">{item.title}</div>
                          <div className="ag-details__event-desc">{item.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}

              {detailTab === "Merchants" && (
                agentDetailLoading ? (
                  <div className="ag-details__placeholder">Loading merchants…</div>
                ) : detailMerchants.length === 0 ? (
                  <div className="ag-details__placeholder">
                    {num(detailInfo.merchantCount ?? detailInfo.totalMerchants ?? selectedAgent.merchants) > 0
                      ? `${num(detailInfo.merchantCount ?? detailInfo.totalMerchants ?? selectedAgent.merchants)} merchants onboarded. Details available in View Merchants.`
                      : "No merchants onboarded yet."}
                  </div>
                ) : (
                  <div className="ag-details__timeline">
                    {detailMerchants.map((m, idx) => (
                      <div key={m._id ?? m.id ?? m.merchantID ?? idx} className="ag-details__event">
                        <span className={`ag-details__dot ${idx === 0 ? "active" : ""}`} />
                        <div>
                          <div className="ag-details__event-title">{m.name ?? m.businessName ?? m.merchantName ?? "Merchant"}</div>
                          <div className="ag-details__event-desc">
                            {[m.category ?? m.businessType, m.status].filter(Boolean).join(" • ")}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}

              {detailTab === "Compliance" && (
                <div className="ag-details__placeholder">
                  {detailInfo.complianceStatus ?? detailInfo.kycStatus
                    ? `KYC / Compliance status: ${detailInfo.complianceStatus ?? detailInfo.kycStatus}`
                    : "KYC and compliance checks are up to date."}
                </div>
              )}

              <div className="ag-details__actions">
                <button
                  className="ag-details__btn ag-details__btn--primary"
                  type="button"
                  disabled
                  aria-disabled="true"
                >
                  Initiate Message
                </button>
                {selectedAgent.status === "inactive" ? (
                  <button
                    className="ag-details__btn ag-details__btn--ghost"
                    onClick={() =>
                      setConfirmAction({
                        title: "Activate Account",
                        message: `Activate account for ${selectedAgent.name}?`,
                        confirmLabel: "Activate",
                        onConfirm: () => {
                          handleActivateAgent(selectedAgent);
                          setSelectedAgent(null);
                        },
                      })
                    }
                  >
                    Activate Account
                  </button>
                ) : (
                  <button
                    className="ag-details__btn ag-details__btn--ghost"
                    onClick={() =>
                      setConfirmAction({
                        title: "Suspend Account",
                        message: `Suspend account for ${selectedAgent.name}?`,
                        confirmLabel: "Suspend",
                        onConfirm: () => {
                          handleSuspendAgent(selectedAgent);
                          setSelectedAgent(null);
                        },
                      })
                    }
                  >
                    Suspend Account
                  </button>
                )}
              </div>
            </div>
          </aside>
        </div>
      )}
      {confirmModal}
    </div>
  );
};

export default AgentsList;
